const React = require('react');
const { default: Checkbox } = require('@mui/material/Checkbox');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');
const { default: HelpOutlineIcon } = require('@mui/icons-material/HelpOutline');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: Slider } = require('@mui/material/Slider');
const { default: Styled } = require('styled-components');
const { default: Tooltip } = require('@mui/material/Tooltip');
const { default: Typography } = require('@mui/material/Typography');
const FieldContext = require('./FieldContext');

const internals = {};

module.exports = function SliderWithOptOutField(props) {
    const {
        FieldRoot,
        FieldHeader,
        FieldTitle,
        HelperText,
        OptOutLabel,
        RequiredAsterisk,
        SelectedLabel,
        SliderCaptionRow,
        SliderWrapper,
        UnsetPrompt
    } = internals;

    const {
        field = {},
        fieldName = field.name
    } = props;
    const fieldController = FieldContext.useField(fieldName);
    const currentValue = fieldController.getValue();
    const optOutValue = internals.resolveOptOutValue(field);
    const optedOut = internals.isOptedOut({
        value: currentValue,
        optOutValue
    });
    const isUnset = currentValue === null && !optedOut;
    const min = field.min || 1;
    const max = field.max || 5;
    const fallbackValue = field.defaultOptInValue || field.default || min;
    const uncheckedOptOutValue = field.default === null ? null : fallbackValue;
    const numericValue = currentValue !== null ? Number(currentValue) : NaN;
    const resolvedValue = !optedOut && Number.isFinite(numericValue) ? numericValue : fallbackValue;
    const centerValue = field.centerValue || ((min + max) / 2);
    const selectedOption = !isUnset && field.options?.find((opt) => opt.value === resolvedValue);
    const colors = {
        rail: 'linear-gradient(to right, #4611aa, #ff4082)',
        ...(field.colors || {})
    };
    const title = field.title || field.label;
    const errorText = fieldController.getError?.();
    const hasError = !!(fieldController.hasError?.() || errorText);
    const helperText = errorText || field.helperText || null;
    const helperTextId = helperText ? `${fieldName}-helper-text` : undefined;

    return <FieldRoot
        ref={fieldController.inputRef}
        aria-describedby={helperTextId}
        aria-invalid={hasError || undefined}
        data-form-field-name={fieldName}
        tabIndex={hasError ? -1 : undefined}
    >
        <FieldHeader>
            <FieldTitle>
                {title}
                {field.required && <RequiredAsterisk aria-hidden='true'>{'*'}</RequiredAsterisk>}
                {internals.hasTooltip(field) && <Tooltip
                    arrow
                    enterTouchDelay={0}
                    leaveTouchDelay={6000}
                    title={internals.getTooltipContent(field)}
                >
                    <IconButton
                        aria-label={field.tooltipAriaLabel || `${title} descriptions`}
                        size='small'
                        style={{ color: '#757575' }}
                    >
                        <HelpOutlineIcon fontSize='small' />
                    </IconButton>
                </Tooltip>}
            </FieldTitle>
        </FieldHeader>
        <OptOutLabel
            control={<Checkbox
                checked={optedOut}
                onBlur={fieldController.onBlur}
                onFocus={fieldController.onFocus}
                onChange={(event) => {
                    const nextValue = event.target.checked ? optOutValue : uncheckedOptOutValue;
                    fieldController.setValue(nextValue, undefined, event);
                }}
            />}
            label={field.optOutLabel || 'Skip'}
        />
        {helperText && <HelperText
            id={helperTextId}
            variant='caption'
            $hasError={hasError}
        >
            {helperText}
        </HelperText>}
        {!optedOut && <SliderWrapper>
            {isUnset && !helperText && <UnsetPrompt variant='caption' color='textSecondary'>Tap to set your level</UnsetPrompt>}
            {!!selectedOption && <SelectedLabel variant='caption'>{selectedOption.label}</SelectedLabel>}
            <Slider
                aria-describedby={helperTextId}
                aria-invalid={hasError || undefined}
                aria-label={title}
                value={isUnset ? min + 0.5 : resolvedValue}
                min={min}
                max={max}
                step={field.step || 1}
                marks={internals.getMarks(field)}
                valueLabelDisplay={field.valueLabelDisplay || 'off'}
                sx={{
                    '& .MuiSlider-rail': {
                        background: colors.rail,
                        opacity: 1,
                        height: 8,
                        border: 'none'
                    },
                    '& .MuiSlider-track': {
                        display: 'none'
                    },
                    '& .MuiSlider-mark': {
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: '2px solid rgba(0, 0, 0, 0.35)',
                        backgroundColor: '#fff',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        opacity: 1
                    },
                    '& .MuiSlider-markActive': {
                        backgroundColor: '#fff',
                        border: '2px solid rgba(0, 0, 0, 0.35)',
                        opacity: 1
                    },
                    '& .MuiSlider-thumb': {
                        width: 20,
                        height: 20,
                        backgroundColor: 'rgba(0, 0, 0, 0.54)',
                        border: '2px solid #fff',
                        ...(isUnset && { display: 'none' })
                    },
                    ...(field.sliderSx || {})
                }}
                onBlur={fieldController.onBlur}
                onFocus={fieldController.onFocus}
                onChange={(event, value) => {
                    fieldController.setValue(value, undefined, event);
                }}
            />
            {!!field.captions?.length && <SliderCaptionRow>
                {field.captions.map((caption) => (
                    <Typography key={caption} variant='caption' color='textSecondary'>{caption}</Typography>
                ))}
            </SliderCaptionRow>}
        </SliderWrapper>}
    </FieldRoot>;
};

internals.resolveOptOutValue = (field = {}) => Object.prototype.hasOwnProperty.call(field, 'optOutValue')
    ? field.optOutValue
    : 'null';

internals.isOptedOut = ({
    value,
    optOutValue
}) => {

    if (value === null) {
        return optOutValue === null;
    }

    return value === optOutValue;
};

internals.getMarks = (field = {}) => {

    if (field.marks) {
        return field.marks;
    }

    if (field.options) {
        return field.options.map((option) => ({ value: option.value }));
    }

    return true;
};

internals.hasTooltip = (field = {}) => !!field.tooltipContent || !!field.tooltipSections?.length;

internals.getTooltipContent = (field = {}) => {

    if (field.tooltipContent) {
        return field.tooltipContent;
    }

    return <div style={{ lineHeight: 1.4 }}>
        {field.tooltipSections.map((section, index) => (
            <div key={`${section.title}-${index}`} style={index ? { marginTop: 4 } : null}>
                <strong>{section.title}:</strong> {section.description}
            </div>
        ))}
    </div>;
};

internals.FieldRoot = Styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    outline: none;
    width: 100%;
`;

internals.FieldHeader = Styled.div`
    align-items: center;
    column-gap: ${({ theme }) => theme.spacing(1.5)};
    display: flex;
    flex-flow: row wrap;
    margin-bottom: ${({ theme }) => theme.spacing(1.25)};
    row-gap: ${({ theme }) => theme.spacing(.5)};
`;

internals.FieldTitle = Styled.span`
    align-items: center;
    display: inline-flex;
    gap: ${({ theme }) => theme.spacing(.75)};
    line-height: 1.2;
`;

internals.RequiredAsterisk = Styled.span`
    color: ${({ theme }) => theme.palette.error.main};
    margin-left: ${({ theme }) => theme.spacing(0.25)};
`;

internals.OptOutLabel = Styled(FormControlLabel)`
    && {
        margin-left: 0;
        margin-right: 0;
    }
`;

internals.HelperText = Styled(Typography)`
    && {
        color: ${({ $hasError, theme }) => $hasError ? theme.palette.error.main : theme.palette.text.secondary};
        display: block;
        margin-bottom: ${({ theme }) => theme.spacing(0.5)};
        margin-top: ${({ theme }) => theme.spacing(-0.25)};
    }
`;

internals.SliderWrapper = Styled.div`
    box-sizing: border-box;
    min-width: 0;
    padding: 0 ${({ theme }) => theme.spacing(1.5)};
    width: 100%;
`;

internals.SelectedLabel = Styled(Typography)`
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing(0.5)};
    text-align: center;
`;

internals.UnsetPrompt = Styled(Typography)`
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing(0.5)};
    text-align: center;
`;

internals.SliderCaptionRow = Styled.div`
    display: grid;
    gap: ${({ theme }) => theme.spacing(1)};
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin-top: ${({ theme }) => theme.spacing(-.75)};

    & > *:nth-child(1) {
        text-align: left;
    }

    & > *:nth-child(2) {
        text-align: center;
    }

    & > *:nth-child(3) {
        text-align: right;
    }
`;

module.exports.internals = internals;
